import { OfflineDBManager, SyncOperation, ConflictRecord } from './offline-db-manager';
import { apiClient } from '../../services/api';

/** Browser-compatible EventEmitter (replaces Node.js 'events' module) */
class EventEmitter {
  private _listeners = new Map<string, ((...args: any[]) => void)[]>();

  on(event: string, fn: (...args: any[]) => void): this {
    const list = this._listeners.get(event) ?? [];
    list.push(fn);
    this._listeners.set(event, list);
    return this;
  }

  emit(event: string, ...args: any[]): boolean {
    const list = this._listeners.get(event);
    if (!list?.length) return false;
    for (const fn of list) fn(...args);
    return true;
  }

  removeAllListeners(event?: string): this {
    if (event) this._listeners.delete(event);
    else this._listeners.clear();
    return this;
  }
}

export interface SyncResult {
  processed: number;
  succeeded: number;
  failed: number;
  conflicts: number;
}

export interface SyncProgress {
  total: number;
  current: number;
  currentOperation?: SyncOperation;
  status: 'idle' | 'syncing' | 'paused' | 'error';
  errorMessage?: string;
}

export class SyncQueueManager extends EventEmitter {
  private dbManager: OfflineDBManager;
  private syncInProgress = false;
  private syncAbortController?: AbortController;
  private retryTimers = new Map<string, NodeJS.Timeout>();

  constructor(dbManager: OfflineDBManager) {
    super();
    this.dbManager = dbManager;
  }

  async queueOperation(
    operation: Omit<SyncOperation, 'id' | 'timestamp' | 'retryCount' | 'status'>
  ): Promise<string> {
    const fullOperation: SyncOperation = {
      ...operation,
      id: this.generateOperationId(),
      timestamp: new Date().toISOString(),
      retryCount: 0,
      status: 'pending',
    };

    await this.dbManager.storeSyncOperation(fullOperation);
    
    // Emit event for UI updates
    this.emit('operation-queued', fullOperation);
    
    // Trigger sync if online
    if (navigator.onLine && !this.syncInProgress) {
      this.processSyncQueue();
    }
    
    return fullOperation.id;
  }

  async processSyncQueue(): Promise<SyncResult> {
    if (this.syncInProgress) {
      console.log('[SyncQueue] Sync already in progress');
      return { processed: 0, succeeded: 0, failed: 0, conflicts: 0 };
    }

    this.syncInProgress = true;
    this.syncAbortController = new AbortController();
    
    const result: SyncResult = {
      processed: 0,
      succeeded: 0,
      failed: 0,
      conflicts: 0,
    };

    try {
      const pendingOperations = await this.dbManager.getPendingSyncOperations();
      const totalOperations = pendingOperations.length;
      
      // Emit sync started event
      this.emit('sync-started', { total: totalOperations });

      // Sort by priority and timestamp
      const sortedOperations = this.sortOperations(pendingOperations);

      for (const operation of sortedOperations) {
        if (this.syncAbortController?.signal?.aborted) {
          break;
        }

        // Check dependencies
        if (operation.dependsOn && !await this.dependenciesCompleted(operation.dependsOn)) {
          console.log(`[SyncQueue] Skipping operation ${operation.id} - dependencies not met`);
          continue;
        }

        result.processed++;
        
        // Emit progress
        this.emit('sync-progress', {
          total: totalOperations,
          current: result.processed,
          currentOperation: operation,
          status: 'syncing',
        } as SyncProgress);
        
        try {
          await this.processSingleOperation(operation);
          await this.markOperationCompleted(operation.id);
          result.succeeded++;
        } catch (error: any) {
          if (error.type === 'conflict') {
            await this.handleConflict(operation, error);
            result.conflicts++;
          } else {
            await this.handleOperationFailure(operation, error);
            result.failed++;
          }
        }
      }

      // Process any remaining retry operations
      await this.processRetryQueue();

    } catch (error) {
      console.error('[SyncQueue] Sync failed:', error);
      this.emit('sync-error', error);
    } finally {
      this.syncInProgress = false;
      this.syncAbortController = undefined;
      
      // Emit sync completed event
      this.emit('sync-completed', result);
    }

    return result;
  }

  private sortOperations(operations: SyncOperation[]): SyncOperation[] {
    return operations.sort((a, b) => {
      // Priority first (higher priority = lower number)
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      
      // Then by timestamp (older first)
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    });
  }

  private async dependenciesCompleted(dependencyIds: string[]): Promise<boolean> {
    for (const depId of dependencyIds) {
      const operation = await this.getOperation(depId);
      if (!operation || operation.status !== 'completed') {
        return false;
      }
    }
    return true;
  }

  private async processSingleOperation(operation: SyncOperation): Promise<void> {
    await this.dbManager.updateSyncOperationStatus(operation.id, 'syncing');
    
    switch (operation.type) {
      case 'create':
        return this.syncCreate(operation);
      case 'update':
        return this.syncUpdate(operation);
      case 'delete':
        return this.syncDelete(operation);
      default:
        throw new Error(`Unknown operation type: ${operation.type}`);
    }
  }

  private async syncCreate(operation: SyncOperation): Promise<void> {
    const endpoint = this.getApiEndpoint(operation.resourceType);
    
    try {
      const response = await apiClient.post(
        endpoint,
        operation.data,
        {
          signal: this.syncAbortController?.signal,
        }
      );

      // Update local cache with server response (may have server-generated ID)
      const responseData = response.data as any;
      await this.updateLocalCache(
        operation.resourceType, 
        responseData.id || operation.resourceId, 
        responseData
      );
      
      // Update operation with server ID if different
      if (responseData.id !== operation.resourceId) {
        await this.updateOperationResourceId(operation.id, responseData.id);
      }
    } catch (error: any) {
      this.handleSyncError(operation, error);
    }
  }

  private async syncUpdate(operation: SyncOperation): Promise<void> {
    const endpoint = this.getApiEndpoint(operation.resourceType, operation.resourceId);
    
    try {
      const response = await apiClient.put(
        endpoint,
        operation.data,
        {
          signal: this.syncAbortController?.signal,
        }
      );

      if (response.status === 409) {
        // Conflict detected
        const conflictData = response.data as any;
        const conflictError = new Error('Sync conflict detected');
        (conflictError as any).type = 'conflict';
        (conflictError as any).localData = operation.data;
        (conflictError as any).remoteData = conflictData.current;
        (conflictError as any).conflictInfo = conflictData.conflict;
        throw conflictError;
      }

      // Update local cache with server response
      await this.updateLocalCache(operation.resourceType, operation.resourceId, response.data);
    } catch (error: any) {
      this.handleSyncError(operation, error);
    }
  }

  private async syncDelete(operation: SyncOperation): Promise<void> {
    const endpoint = this.getApiEndpoint(operation.resourceType, operation.resourceId);
    
    try {
      await apiClient.delete(endpoint, {
        signal: this.syncAbortController?.signal,
      });

      // Remove from local cache
      await this.removeFromLocalCache(operation.resourceType, operation.resourceId);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Already deleted on server, consider it successful
        await this.removeFromLocalCache(operation.resourceType, operation.resourceId);
        return;
      }
      
      this.handleSyncError(operation, error);
    }
  }

  private handleSyncError(operation: SyncOperation, error: any): void {
    operation.retryCount++;
    
    if (error.type === 'conflict') {
      throw error; // Re-throw to handle as conflict
    }
    
    if (operation.retryCount >= 3) {
      throw new Error(`Max retries exceeded: ${error.message}`);
    }
    
    // Schedule retry with exponential backoff
    const delay = Math.pow(2, operation.retryCount) * 1000;
    this.scheduleRetry(operation, delay);
    
    throw new Error(`Operation will be retried: ${error.message}`);
  }

  private scheduleRetry(operation: SyncOperation, delay: number): void {
    const timer = setTimeout(() => {
      this.retryTimers.delete(operation.id);
      this.processSingleOperation(operation).catch(error => {
        console.error(`[SyncQueue] Retry failed for operation ${operation.id}:`, error);
      });
    }, delay);
    
    this.retryTimers.set(operation.id, timer);
  }

  private async processRetryQueue(): Promise<void> {
    // Process any operations scheduled for retry
    const retryOperations = Array.from(this.retryTimers.keys());
    for (const operationId of retryOperations) {
      const operation = await this.getOperation(operationId);
      if (operation && operation.status === 'pending') {
        await this.processSingleOperation(operation).catch(error => {
          console.error(`[SyncQueue] Retry failed for operation ${operationId}:`, error);
        });
      }
    }
  }

  private async handleConflict(operation: SyncOperation, error: any): Promise<void> {
    const conflict: ConflictRecord = {
      id: this.generateConflictId(),
      resourceType: operation.resourceType,
      resourceId: operation.resourceId,
      localVersion: error.localData,
      remoteVersion: error.remoteData,
      conflictType: 'edit',
      timestamp: new Date().toISOString(),
      status: 'pending',
      autoResolvable: this.isAutoResolvable(operation.resourceType, error),
    };

    await this.dbManager.storeConflict(conflict);
    await this.dbManager.updateSyncOperationStatus(operation.id, 'conflict');
    
    // Emit conflict event for UI
    this.emit('conflict-detected', conflict);
    
    // Attempt auto-resolution if possible
    if (conflict.autoResolvable) {
      await this.attemptAutoResolve(conflict, operation);
    }
  }

  private async handleOperationFailure(operation: SyncOperation, error: any): Promise<void> {
    const errorMessage = error.message || 'Unknown error';
    await this.dbManager.updateSyncOperationStatus(operation.id, 'failed', errorMessage);
    
    // Emit failure event
    this.emit('operation-failed', {
      operation,
      error: errorMessage,
    });
  }

  private async markOperationCompleted(operationId: string): Promise<void> {
    await this.dbManager.updateSyncOperationStatus(operationId, 'completed');
    
    // Clean up retry timer if exists
    const timer = this.retryTimers.get(operationId);
    if (timer) {
      clearTimeout(timer);
      this.retryTimers.delete(operationId);
    }
  }

  private isAutoResolvable(resourceType: string, error: any): boolean {
    // Simple heuristics for auto-resolution
    if (resourceType === 'knowledge') {
      // Check if changes are in different fields
      const localKeys = Object.keys(error.localData);
      const remoteKeys = Object.keys(error.remoteData);
      const conflictingKeys = localKeys.filter(key => 
        remoteKeys.includes(key) && 
        error.localData[key] !== error.remoteData[key]
      );
      
      // Auto-resolvable if no overlapping changes
      return conflictingKeys.length === 0;
    }
    
    return false;
  }

  private async attemptAutoResolve(conflict: ConflictRecord, operation: SyncOperation): Promise<void> {
    try {
      // Simple merge strategy - combine non-conflicting fields
      const merged = this.mergeNonConflictingFields(
        conflict.localVersion,
        conflict.remoteVersion
      );
      
      // Update operation with merged data
      operation.data = merged;
      operation.retryCount = 0; // Reset retry count
      
      // Mark conflict as resolved
      await this.dbManager.updateConflictResolution(conflict.id, {
        strategy: 'merge',
        mergedData: merged,
        timestamp: new Date().toISOString(),
      });
      
      // Retry the operation
      await this.processSingleOperation(operation);
      
      // Emit auto-resolved event
      this.emit('conflict-auto-resolved', conflict);
    } catch (error) {
      console.error('[SyncQueue] Auto-resolve failed:', error);
    }
  }

  private mergeNonConflictingFields(local: any, remote: any): any {
    const merged = { ...remote }; // Start with remote as base
    
    // Add local fields that don't exist in remote
    for (const key of Object.keys(local)) {
      if (!(key in remote)) {
        merged[key] = local[key];
      }
    }
    
    // Use newer timestamp
    if (local.lastModified && remote.lastModified) {
      merged.lastModified = new Date(local.lastModified) > new Date(remote.lastModified)
        ? local.lastModified
        : remote.lastModified;
    }
    
    return merged;
  }

  private async updateLocalCache(resourceType: string, resourceId: string, data: any): Promise<void> {
    switch (resourceType) {
      case 'knowledge':
        await this.dbManager.storeKnowledgeNode(data);
        break;
      case 'conversation':
        await this.dbManager.storeConversation(data);
        break;
      // Add other resource types as needed
    }
  }

  private async removeFromLocalCache(resourceType: string, resourceId: string): Promise<void> {
    // Implementation depends on adding delete methods to OfflineDBManager
    console.log(`[SyncQueue] Removing ${resourceType}:${resourceId} from local cache`);
  }

  private async getOperation(operationId: string): Promise<SyncOperation | null> {
    // Implementation would query the sync_queue store
    return null;
  }

  private async updateOperationResourceId(operationId: string, newResourceId: string): Promise<void> {
    // Implementation would update the operation in the sync_queue store
    console.log(`[SyncQueue] Updating operation ${operationId} with new resource ID ${newResourceId}`);
  }

  private getApiEndpoint(resourceType: string, resourceId?: string): string {
    const baseUrl = '/api';
    
    switch (resourceType) {
      case 'knowledge':
        return resourceId ? `${baseUrl}/knowledge/${resourceId}` : `${baseUrl}/knowledge`;
      case 'conversation':
        return resourceId ? `${baseUrl}/conversations/${resourceId}` : `${baseUrl}/conversations`;
      case 'user':
        return resourceId ? `${baseUrl}/users/${resourceId}` : `${baseUrl}/users`;
      case 'integration':
        return resourceId ? `${baseUrl}/integrations/${resourceId}` : `${baseUrl}/integrations`;
      default:
        throw new Error(`Unknown resource type: ${resourceType}`);
    }
  }

  private generateOperationId(): string {
    return `op-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private generateConflictId(): string {
    return `conflict-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  pauseSync(): void {
    if (this.syncAbortController && this.syncAbortController.signal && !this.syncAbortController.signal.aborted) {
      this.syncAbortController.abort();
    }
    
    // Clear all retry timers
    Array.from(this.retryTimers.values()).forEach(timer => {
      clearTimeout(timer);
    });
    this.retryTimers.clear();
    
    this.emit('sync-paused');
  }

  resumeSync(): void {
    if (!this.syncInProgress && navigator.onLine) {
      this.processSyncQueue();
    }
  }

  async getQueueStatus(): Promise<{
    pending: number;
    syncing: number;
    failed: number;
    conflicts: number;
  }> {
    const operations = await this.dbManager.getPendingSyncOperations();
    
    return {
      pending: operations.filter(op => op.status === 'pending').length,
      syncing: operations.filter(op => op.status === 'syncing').length,
      failed: operations.filter(op => op.status === 'failed').length,
      conflicts: operations.filter(op => op.status === 'conflict').length,
    };
  }

  destroy(): void {
    this.pauseSync();
    this.removeAllListeners();
  }
}